/**
 * /publisher-payout-overrides — operator console route (FS-821).
 *
 * Renders the per-affiliate payout override manager. The root wire-up cycle
 * picks this file up after prod merge; agents never touch router.tsx.
 *
 * Search param `?affiliate=<id>` deep-links to a specific affiliate's
 * override list; invalid values are silently dropped by `validateSearch`.
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PublisherPayoutOverridesPage } from "@/features/publisher-payout-overrides/PublisherPayoutOverridesPage";

interface PublisherPayoutOverridesSearch {
	affiliate?: string;
}

const AFFILIATE_RE = /^[a-z0-9_-]{1,64}$/i;

export const publisherPayoutOverridesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/publisher-payout-overrides",
	validateSearch: (raw: Record<string, unknown>): PublisherPayoutOverridesSearch => {
		const affiliate = raw.affiliate;
		if (typeof affiliate === "string" && AFFILIATE_RE.test(affiliate)) {
			return { affiliate };
		}
		return {};
	},
	component: PublisherPayoutOverridesRouteShell,
});

export default publisherPayoutOverridesRoute;

// ---------------------------------------------------------------------------
// Route shell — breadcrumb + live feature page.
// ---------------------------------------------------------------------------

export function PublisherPayoutOverridesRouteShell() {
	const { affiliate } = publisherPayoutOverridesRoute.useSearch();
	return (
		<div className="flex w-full flex-col">
			<Breadcrumb />
			<PublisherPayoutOverridesPage initialAffiliateId={affiliate} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav
			aria-label="Breadcrumb"
			className="mx-auto flex w-full max-w-7xl items-center gap-2 px-6 pt-4 text-sm text-zinc-500"
		>
			<Link
				to="/"
				className="rounded-md px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
			>
				Dashboard
			</Link>
			<span aria-hidden="true" className="text-zinc-300">
				/
			</span>
			<span className="rounded-md px-1 py-0.5">Publishers</span>
			<span aria-hidden="true" className="text-zinc-300">
				/
			</span>
			<span className="text-zinc-900 font-medium">Payout Overrides</span>
		</nav>
	);
}
